import type { AuthType } from '@/features/auth/components/GlobalAuthModal';
import { type OryKratosErrorResponse } from './types';

/**
 * Axiosエラーから再認証が必要かどうかを判定
 *
 * @param error - Axiosエラーオブジェクト
 * @returns 再認証が必要な場合はAuthType、不要な場合はnull
 */
export async function checkReauthRequiredFromAxiosError(error: unknown): Promise<AuthType | null> {
  // AxiosErrorかどうかを確認
  if (!error || typeof error !== 'object' || !('response' in error) || !error.response) {
    return null;
  }
  const axiosError = error as {
    response: {
      status: number;
      data?: unknown;
    };
  };
  if (axiosError.response.status !== 403) {
    return null;
  }
  try {
    const errorData = axiosError.response.data as OryKratosErrorResponse;
    const errorId = errorData?.error?.id;
    if (errorId === 'session_refresh_required') {
      return 'refresh';
    }
    if (errorId === 'session_aal2_required') {
      return 'aal2';
    }
    return null;
  } catch {
    // パースに失敗した場合は再認証不要
    return null;
  }
}

/**
 * Responseオブジェクトから再認証が必要かどうかを判定
 *
 * @param response - fetch APIのResponseオブジェクト（既にクローンされていることを想定）
 * @returns 再認証が必要な場合はAuthType、不要な場合はnull
 */
export async function checkReauthRequiredFromResponse(response: Response): Promise<AuthType | null> {
  if (response.status !== 403) {
    return null;
  }
  try {
    // レスポンスが既に読み取られている可能性があるため、再度クローンを試みる
    // ただし、既にクローンされている場合はそのまま使用
    let responseToRead: Response;
    try {
      responseToRead = response.clone();
    } catch {
      // クローンできない場合は、元のレスポンスを使用（既にクローンされている可能性）
      // ただし、元のレスポンスも既に読み取られている可能性がある
      responseToRead = response;
    }
    const data = await responseToRead.json();
    const errorData = data as OryKratosErrorResponse;
    const errorId = errorData?.error?.id;
    if (errorId === 'session_refresh_required') {
      return 'refresh';
    }
    if (errorId === 'session_aal2_required') {
      return 'aal2';
    }
    return null;
  } catch {
    // JSONのパースに失敗した場合は再認証不要
    return null;
  }
}